import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Sparkles, Zap, Code, Database } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';

const NewProjectPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const template = location.state?.template;
  
  const [projectData, setProjectData] = useState({
    description: template?.description || '',
    style: template?.style || 'modern',
    stack: template?.stack || 'react-fastapi',
    aiModel: template?.aiModel || 'auto'
  });

  const [projectName, setProjectName] = useState(template?.name || '');

  useEffect(() => {
    if (template) {
      setProjectName(template.name);
      setProjectData({
        description: template.description,
        style: template.style,
        stack: template.stack,
        aiModel: template.aiModel
      });
    }
  }, [template]);

  const [isCreating, setIsCreating] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const res = await api.get('/models');
        setAvailableModels(res.data.models || []);
      } catch {
        setAvailableModels([
          { value: 'auto', name: 'Auto', description: 'Smart routing', badge: 'Smart' },
          { value: 'claude', name: 'Claude Sonnet 4', description: 'Anthropic — stable' },
          { value: 'openai', name: 'GPT-5.2', description: 'OpenAI — flagship' },
        ]);
      }
    };
    fetchModels();
  }, []);

  const handleCreate = async () => {
    if (!projectName.trim()) {
      toast({ title: 'Error', description: 'Please enter a project name', variant: 'destructive' });
      return;
    }
    if (!projectData.description.trim()) {
      toast({ title: 'Error', description: 'Please describe your project', variant: 'destructive' });
      return;
    }

    setIsCreating(true);
    try {
      const response = await api.post('/projects', {
        name: projectName.trim(),
        description: projectData.description.trim(),
        style: projectData.style,
        stack: projectData.stack,
        aiModel: projectData.aiModel
      });

      toast({
        title: 'Project Created!',
        description: 'Your AI agents are starting to build your app.'
      });

      navigate('/dashboard', { state: { newProjectId: response.data.id } });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create project',
        variant: 'destructive'
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-2 text-foreground/80 hover:text-foreground hover:bg-accent"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-blue-400" />
            {template ? `Create ${template.name}` : 'Create New Project'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {template ? 'Customize your AI app template' : 'Describe your app and let AI build it for you'}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {template && (
          <Card className="border-2 border-blue-500/30 bg-blue-500/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <span className="text-4xl">{template.icon || '~'}</span>
                <div>
                  <h3 className="font-semibold text-blue-300">Using Template: {template.name}</h3>
                  <p className="text-sm text-blue-400/70">Pre-configured for optimal results</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Project Name */}
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <Label htmlFor="projectName" className="text-lg font-semibold text-foreground">
              Project Name
            </Label>
            <Input
              id="projectName"
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="My Awesome AI App"
              className="mt-3 text-base bg-background border-input text-foreground placeholder:text-muted-foreground"
            />
          </CardContent>
        </Card>

        {/* Project Description */}
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <Label htmlFor="description" className="text-lg font-semibold text-foreground">
              What do you want to build?
            </Label>
            <Textarea
              id="description"
              value={projectData.description}
              onChange={(e) => setProjectData({ ...projectData, description: e.target.value })}
              placeholder="Describe your app in detail..."
              className="mt-3 min-h-[200px] text-base bg-background border-input text-foreground placeholder:text-muted-foreground"
            />
            <p className="text-sm text-muted-foreground mt-2">
              Be as detailed as possible - mention features, design preferences, and functionality.
            </p>
          </CardContent>
        </Card>

        {/* Style Selection */}
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <Label className="text-lg font-semibold mb-4 block text-foreground">
              Choose Design Style
            </Label>
            <RadioGroup
              value={projectData.style}
              onValueChange={(value) => setProjectData({ ...projectData, style: value })}
              className="grid grid-cols-3 gap-4"
            >
              {[
                { value: 'modern', icon: <Zap className="w-8 h-8 text-blue-400 mb-2" />, label: 'Modern', desc: 'Clean, minimal design' },
                { value: 'gradient', icon: <Sparkles className="w-8 h-8 text-purple-400 mb-2" />, label: 'Gradient', desc: 'Colorful, vibrant look' },
                { value: 'minimal', icon: <Code className="w-8 h-8 text-muted-foreground mb-2" />, label: 'Minimal', desc: 'Simple, focused UI' }
              ].map(style => (
                <div key={style.value}>
                  <RadioGroupItem value={style.value} id={style.value} className="peer sr-only" />
                  <Label
                    htmlFor={style.value}
                    className="flex flex-col items-center justify-between rounded-lg border-2 border-border bg-muted p-4 hover:bg-secondary peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-500/10 cursor-pointer transition-all"
                  >
                    {style.icon}
                    <span className="font-medium text-foreground">{style.label}</span>
                    <span className="text-xs text-muted-foreground text-center mt-1">{style.desc}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Tech Stack Selection */}
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <Label className="text-lg font-semibold mb-4 block text-foreground">
              Technology Stack
            </Label>
            <RadioGroup
              value={projectData.stack}
              onValueChange={(value) => setProjectData({ ...projectData, stack: value })}
              className="space-y-3"
            >
              {[
                { value: 'react-fastapi', icon: <Database className="w-6 h-6 text-blue-400" />, title: 'React + FastAPI + MongoDB', desc: 'Full-stack with Python backend', badge: 'Recommended', badgeColor: 'bg-blue-500/20 text-blue-400' },
                { value: 'react-node', icon: <Database className="w-6 h-6 text-emerald-400" />, title: 'React + Node.js + PostgreSQL', desc: 'JavaScript full-stack', badge: null }
              ].map(stack => (
                <div key={stack.value}>
                  <RadioGroupItem value={stack.value} id={stack.value} className="peer sr-only" />
                  <Label
                    htmlFor={stack.value}
                    className="flex items-center justify-between rounded-lg border-2 border-border bg-muted p-4 hover:bg-secondary peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-500/10 cursor-pointer transition-all"
                  >
                    <div className="flex items-center gap-3">
                      {stack.icon}
                      <div>
                        <div className="font-medium text-foreground">{stack.title}</div>
                        <div className="text-sm text-muted-foreground">{stack.desc}</div>
                      </div>
                    </div>
                    {stack.badge && <span className={`text-xs ${stack.badgeColor} px-2 py-1 rounded`}>{stack.badge}</span>}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        {/* AI Model Selection */}
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <Label className="text-lg font-semibold mb-4 block text-foreground">
              AI Model
            </Label>
            <RadioGroup
              value={projectData.aiModel}
              onValueChange={(value) => setProjectData({ ...projectData, aiModel: value })}
              className="space-y-3"
            >
              {(availableModels.length > 0 ? availableModels : [
                { value: 'auto', name: 'Auto', description: 'Smart routing', badge: 'Smart' },
              ]).map(model => (
                <div key={model.value}>
                  <RadioGroupItem value={model.value} id={model.value} className="peer sr-only" />
                  <Label
                    htmlFor={model.value}
                    className="flex items-center justify-between rounded-lg border-2 border-border bg-muted p-4 hover:bg-secondary peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-500/10 cursor-pointer transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <Sparkles className="w-6 h-6" style={{ color: model.color || '#8b5cf6' }} />
                      <div>
                        <div className="font-medium text-foreground">{model.name}</div>
                        <div className="text-sm text-muted-foreground">{model.description}</div>
                      </div>
                    </div>
                    {model.badge && <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">{model.badge}</span>}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            data-testid="start-building-btn"
            onClick={handleCreate}
            disabled={isCreating}
            className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-6 text-lg font-semibold rounded-xl shadow-lg shadow-blue-500/20"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            {isCreating ? 'Creating...' : 'Start Building'}
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="px-8 py-6 text-lg rounded-xl border-input text-foreground/80 hover:bg-secondary"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewProjectPage;
